import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/StatusPill";
import { OrigemPill } from "@/components/OrigemPill";
import { buildWaLink } from "@/components/WhatsAppButton";
import { generateVoucherPDF } from "@/lib/voucher";
import { generateReceiptPDF } from "@/lib/receipt";
import { Plus, Trash2, XCircle, LogIn, LogOut, MessageCircle, FileDown, Tag, Receipt, Briefcase, Users, Eye, Pencil, DollarSign, Filter } from "lucide-react";
import { toast } from "sonner";

// F-MOTOR-RESERVAS.3.1.5 — valores canônicos (devem casar com backend).
const FILTER_NONE = "__all__";
const ORIGEM_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "site", label: "Site" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "orcamento", label: "Orçamento" },
];
const STATUS_OPTIONS = [
  { value: "pre-reserva", label: "Pré-reserva" },
  { value: "hospedado", label: "Hospedado" },
  { value: "ativa", label: "Ativa" },
  { value: "finalizada", label: "Finalizada" },
  { value: "cancelada", label: "Cancelada" },
];

const NONE = "__none__";
const PESSOAS_SLOTS = [1, 2, 3, 4, 5, 6];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysBetween(a, b) {
  if (!a || !b) return 0;
  const d = (new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24);
  return d > 0 ? Math.round(d) : 0;
}

function SinalSection({ reserva, onChange, contasFinanceiras = [] }) {
  const [valor, setValor] = useState(reserva?.sinal_valor ? String(reserva.sinal_valor) : "");
  const [metodo, setMetodo] = useState(reserva?.sinal_metodo || "pix");
  const [gateway, setGateway] = useState(reserva?.sinal_gateway || "");
  const [cfSinalId, setCfSinalId] = useState("");
  const [busy, setBusy] = useState(false);
  const [pagbankChk, setPagbankChk] = useState(null);  // F15.PB.3E.1: checkout PagBank real retornado pelo backend

  useEffect(() => {
    setValor(reserva?.sinal_valor ? String(reserva.sinal_valor) : "");
    setMetodo(reserva?.sinal_metodo || "pix");
    setGateway(reserva?.sinal_gateway || "");
    setPagbankChk(null);
  }, [reserva?.id, reserva?.sinal_status, reserva?.sinal_token]);

  const status = reserva?.sinal_status || "sem_sinal";
  const link = reserva?.sinal_link || "";
  const jaEnviouWa = !!reserva?.sinal_enviado_whatsapp_em;
  const jaEnviouEmail = !!reserva?.sinal_enviado_email_em;

  async function gerar() {
    if (!Number(valor)) { toast.error("Informe o valor do sinal"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/reservas/${reserva.id}/sinal`,
        { valor: Number(valor), metodo, gateway: gateway || "" });
      toast.success("Link de sinal gerado");
      onChange?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar sinal");
    } finally { setBusy(false); }
  }

  async function copiar() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado");
    } catch { toast.error("Falha ao copiar. Copie manualmente."); }
  }

  async function enviar(canal) {
    const label = canal === "whatsapp" ? "WhatsApp" : "e-mail";
    if (!window.confirm(`Confirmar envio do link de sinal por ${label}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reservas/${reserva.id}/sinal/enviar`,
        { canal, autorizado: true });
      toast.success(`Envio por ${label} registrado`);
      onChange?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erro ao enviar por ${label}`);
    } finally { setBusy(false); }
  }

  async function marcarPago() {
    if (!cfSinalId) { toast.error("Selecione a Conta Financeira do recebimento."); return; }
    if (!window.confirm("Confirmar recebimento do sinal? Um pagamento será registrado.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reservas/${reserva.id}/sinal/pagar`, {
        conta_financeira_id: cfSinalId,
      });
      toast.success("Sinal marcado como pago");
      onChange?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar como pago");
    } finally { setBusy(false); }
  }

  async function cancelar() {
    if (!window.confirm("Cancelar o sinal e invalidar o link?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/reservas/${reserva.id}/sinal`);
      toast.success("Sinal cancelado");
      onChange?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar sinal");
    } finally { setBusy(false); }
  }

  // F15.PB.3E.1 — dispara o checkout PagBank real (endpoint já existente no backend).
  // Só faz chamada ao clicar; nada é executado automaticamente ao abrir a tela.
  async function gerarLinkPagbank() {
    setBusy(true);
    try {
      const { data } = await api.post(`/reservas/${reserva.id}/sinal/gerar-link-pagbank`);
      setPagbankChk(data);
      toast.success(data?.already_existed
        ? "Checkout PagBank já existente recuperado"
        : "Checkout PagBank criado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar link PagBank");
    } finally { setBusy(false); }
  }

  async function copiarPagbankLink() {
    if (!pagbankChk?.link) return;
    try {
      await navigator.clipboard.writeText(pagbankChk.link);
      toast.success("Link PagBank copiado");
    } catch { toast.error("Falha ao copiar. Copie manualmente."); }
  }

  const statusTone = {
    pago: "bg-[#EAF0EA] text-[#083A6B]",
    pendente: "bg-[#FDF3E3] text-[#8B6A1F]",
    cancelado: "bg-stone-200 text-stone-600",
    sem_sinal: "bg-white text-stone-500 border border-stone-200",
  }[status] || "bg-stone-100 text-stone-700";

  return (
    <div data-testid="sinal-section" className="mt-5 pt-4 border-t border-stone-200">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Sinal / Entrada</div>
        <span data-testid="sinal-status-badge" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${statusTone}`}>
          {status === "sem_sinal" ? "sem sinal" : status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <Label className="text-xs">Valor do sinal</Label>
          <Input type="number" min="0" step="0.01" data-testid="sinal-input-valor"
            value={valor} onChange={(e) => setValor(e.target.value)} placeholder="Ex: 200,00"
            disabled={status === "pago"} />
        </div>
        <div>
          <Label className="text-xs">Método</Label>
          <Select value={metodo} onValueChange={setMetodo} disabled={status === "pago"}>
            <SelectTrigger data-testid="sinal-select-metodo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pix">PIX</SelectItem>
              <SelectItem value="cartao">Cartão</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Gateway (preparação)</Label>
          <Select value={gateway || NONE} onValueChange={(v) => setGateway(v === NONE ? "" : v)} disabled={status === "pago"}>
            <SelectTrigger data-testid="sinal-select-gateway"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              <SelectItem value="pagbank">PagBank (em breve)</SelectItem>
              <SelectItem value="infinitepay">InfinitePay (em breve)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button size="sm" data-testid="btn-gerar-sinal" onClick={gerar} disabled={busy || status === "pago"}
          className="bg-[#083A6B] hover:bg-[#22392E] text-white">
          {status === "pendente" ? "Regenerar link" : "Gerar link"}
        </Button>
        {status === "pendente" && (
          <>
            <Button size="sm" variant="outline" data-testid="btn-copiar-link" onClick={copiar} disabled={!link}>
              Copiar link
            </Button>
            <Button size="sm" variant="outline" data-testid="btn-enviar-wa" onClick={() => enviar("whatsapp")} disabled={busy}>
              WhatsApp {jaEnviouWa && "✓"}
            </Button>
            <Button size="sm" variant="outline" data-testid="btn-enviar-email" onClick={() => enviar("email")} disabled={busy}>
              E-mail {jaEnviouEmail && "✓"}
            </Button>
            <div className="min-w-[200px]">
              <Select value={cfSinalId} onValueChange={setCfSinalId}>
                <SelectTrigger data-testid="sinal-select-conta-financeira" className="h-9">
                  <SelectValue placeholder="Conta Financeira *" />
                </SelectTrigger>
                <SelectContent>
                  {contasFinanceiras.length === 0 ? (
                    <SelectItem value="__none__" disabled>Nenhuma conta ativa cadastrada</SelectItem>
                  ) : (
                    contasFinanceiras.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`opt-sinal-conta-fin-${c.id}`}>
                        {c.nome}{c.banco ? ` — ${c.banco}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" data-testid="btn-marcar-pago-sinal" onClick={marcarPago} disabled={busy || !cfSinalId}
              className="bg-[#D4A652] hover:bg-[#a4462e] text-white disabled:opacity-50">
              Marcar como pago
            </Button>
            <Button size="sm" variant="ghost" data-testid="btn-cancelar-sinal" onClick={cancelar} disabled={busy}
              className="text-stone-500">
              Cancelar sinal
            </Button>
          </>
        )}
      </div>

      {link && status !== "sem_sinal" && (
        <div className="text-xs text-stone-500 break-all">
          <span className="text-stone-400">Link: </span>
          <span data-testid="sinal-link-text" className="text-[#083A6B]">{link}</span>
        </div>
      )}
      {/* F15.PB.3E.1 — botão para gerar checkout PagBank real, aparece só quando faz sentido */}
      {status === "pendente" && gateway === "pagbank" && (
        <div data-testid="pagbank-checkout-panel" className="mt-3 pt-3 border-t border-dashed border-stone-200">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Button size="sm" data-testid="btn-gerar-link-pagbank"
              onClick={gerarLinkPagbank} disabled={busy}
              className="bg-[#083A6B] hover:bg-[#22392E] text-white">
              {busy ? "Gerando…" : (pagbankChk ? "Regerar checkout PagBank" : "Gerar link PagBank")}
            </Button>
            {pagbankChk?.already_existed && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDF3E3] text-[#8B6A1F]">
                idempotente
              </span>
            )}
          </div>
          {pagbankChk?.link && (
            <div className="text-xs text-stone-600 space-y-1">
              <div className="break-all">
                <span className="text-stone-400">Checkout: </span>
                <a data-testid="pagbank-checkout-link" href={pagbankChk.link}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[#083A6B] underline">{pagbankChk.link}</a>
              </div>
              <div className="text-[10px] text-stone-500">
                checkout_id: <span data-testid="pagbank-checkout-id">{pagbankChk.checkout_id}</span>
                {pagbankChk.reference_id && (
                  <> · reference_id: <span data-testid="pagbank-reference-id">{pagbankChk.reference_id}</span></>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" data-testid="btn-copiar-pagbank-link"
                  onClick={copiarPagbankLink}>
                  Copiar link PagBank
                </Button>
                <Button size="sm" variant="outline" data-testid="btn-abrir-pagbank-link"
                  onClick={() => window.open(pagbankChk.link, "_blank", "noopener")}>
                  Abrir link PagBank
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {(jaEnviouWa || jaEnviouEmail) && (
        <div className="text-[10px] text-stone-500 mt-1">
          {jaEnviouWa && <span>WhatsApp enviado em {new Date(reserva.sinal_enviado_whatsapp_em).toLocaleString("pt-BR")}. </span>}
          {jaEnviouEmail && <span>E-mail enviado em {new Date(reserva.sinal_enviado_email_em).toLocaleString("pt-BR")}.</span>}
        </div>
      )}
      <p className="text-[10px] text-stone-400 mt-2">
        Preparado para integração futura com PagBank e InfinitePay. Enquanto o gateway não estiver conectado, o link acima é um placeholder e o pagamento deve ser confirmado manualmente pelo operador.
      </p>
    </div>
  );
}

export default function Reservas() {
  // F-MOTOR-RESERVAS.3.1.5 — filtros sincronizados com a URL (?origem=&status=).
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroOrigem = ORIGEM_OPTIONS.some((o) => o.value === searchParams.get("origem"))
    ? searchParams.get("origem")
    : "";
  const filtroStatus = STATUS_OPTIONS.some((s) => s.value === searchParams.get("status"))
    ? searchParams.get("status")
    : "";
  const hasFiltros = !!(filtroOrigem || filtroStatus);

  const [reservas, setReservas] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [quartos, setQuartos] = useState([]);
  const [hospedes, setHospedes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [reservaDetalhe, setReservaDetalhe] = useState(null);
  const [pagOpen, setPagOpen] = useState(false);
  const [pagReserva, setPagReserva] = useState(null);
  const [pagList, setPagList] = useState([]);
  const [pagForm, setPagForm] = useState({ valor: "", data: "", forma: "pix", observacao: "", conta_financeira_id: "" });
  const [contasFinanceiras, setContasFinanceiras] = useState([]);
  const [tarifaSuggest, setTarifaSuggest] = useState(null);
  const [form, setForm] = useState({
    hospede_id: "", pousada_id: "", quarto_id: "", empresa_id: "",
    data_entrada: todayStr(), data_saida: "",
    num_pessoas: 1,
    valor_diaria: "", observacoes: "",
    cafe_tipo: "nenhum",
    ci_uma_diaria: false,
    ci_bloqueia_suites: true,
  });

  const load = async () => {
    try {
      // F-MOTOR-RESERVAS.3.1.5 — repassa origem/status à API apenas quando setados.
      const reservasParams = {};
      if (filtroOrigem) reservasParams.origem = filtroOrigem;
      if (filtroStatus) reservasParams.status = filtroStatus;
      const [r, p, q, h, e, cf] = await Promise.all([
        api.get("/reservas", { params: reservasParams }),
        api.get("/pousadas"), api.get("/quartos"),
        api.get("/hospedes"), api.get("/empresas"),
        api.get("/contas-financeiras", { params: { ativo: true } }).catch(() => ({ data: [] })),
      ]);
      setReservas(r.data); setPousadas(p.data); setQuartos(q.data);
      setHospedes(h.data); setEmpresas(e.data);
      const cfList = Array.isArray(cf.data) ? cf.data : [];
      setContasFinanceiras(cfList.filter((x) => x?.ativo !== false).sort((a, b) => (a.nome || "").localeCompare(b.nome || "")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar dados de reservas. Tente novamente.");
    }
  };
  // Recarrega quando filtros da URL mudarem.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filtroOrigem, filtroStatus]);

  // F-MOTOR-RESERVAS.3.1.5 — handlers para filtros compactos.
  const updateFiltro = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== FILTER_NONE) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: false });
  };
  const limparFiltros = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("origem");
    next.delete("status");
    setSearchParams(next, { replace: false });
  };

  const quartosByPousada = useMemo(
    () => quartos.filter((x) => x.pousada_id === form.pousada_id && x.status !== "manutencao"),
    [quartos, form.pousada_id]
  );
  const selectedPousada = pousadas.find((x) => x.id === form.pousada_id);
  const isPousadaChacara = !!selectedPousada?.is_chacara;
  const permiteReservaInteira = !!selectedPousada?.permite_reserva_inteira && !isPousadaChacara;
  const chacaraInteiraQuarto = useMemo(
    () => quartosByPousada.find((q) => q.is_chacara_inteira),
    [quartosByPousada],
  );
  const pousadaInteiraQuarto = useMemo(
    () => quartosByPousada.find((q) => q.is_pousada_inteira),
    [quartosByPousada],
  );
  const quartosIndividuais = useMemo(
    () => quartosByPousada.filter((q) => !q.is_chacara_inteira && !q.is_pousada_inteira),
    [quartosByPousada],
  );

  const selectedQuarto = quartos.find((x) => x.id === form.quarto_id);
  const selectedQuartoIsCI = !!selectedQuarto?.is_chacara_inteira;
  const selectedQuartoIsPI = !!selectedQuarto?.is_pousada_inteira;
  const precoDoNumeroPessoas = selectedQuarto?.precos_por_pessoa?.[String(form.num_pessoas)];
  const suggestedValor =
    tarifaSuggest ? Number(tarifaSuggest.valor_diaria)
    : precoDoNumeroPessoas ? Number(precoDoNumeroPessoas)
    : selectedQuarto?.valor_diaria;
  const effValorDiaria = form.valor_diaria !== "" ? Number(form.valor_diaria) : (suggestedValor || 0);
  const diarias = daysBetween(form.data_entrada, form.data_saida);
  const cafeValorPessoa = { nenhum: 0, simples: 22, donna_italia: 32 }[form.cafe_tipo] || 0;
  const cafeTotal = Number(form.num_pessoas || 0) * diarias * cafeValorPessoa;
  const total = diarias * effValorDiaria + cafeTotal;

  const handleHospedeChange = (hospede_id) => {
    const h = hospedes.find((x) => x.id === hospede_id);
    setForm((f) => ({ ...f, hospede_id, empresa_id: h?.empresa_id || "" }));
  };

  useEffect(() => {
    if (!form.pousada_id || !form.data_entrada || !form.data_saida || daysBetween(form.data_entrada, form.data_saida) <= 0) {
      setTarifaSuggest(null);
      return;
    }
    const tipo = selectedQuarto?.tipo || "";
    api.get("/tarifas/lookup", { params: { pousada_id: form.pousada_id, data_entrada: form.data_entrada, data_saida: form.data_saida, tipo_quarto: tipo } })
      .then((r) => setTarifaSuggest(r.data?.tarifa || null))
      .catch(() => setTarifaSuggest(null));
    // Deps granulares em form.X; api/daysBetween/selectedQuarto são referências estáveis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pousada_id, form.quarto_id, form.data_entrada, form.data_saida, selectedQuarto?.tipo]);

  const emptyForm = {
    hospede_id: "", pousada_id: "", quarto_id: "", empresa_id: "",
    data_entrada: todayStr(), data_saida: "", num_pessoas: 1,
    valor_diaria: "", observacoes: "",
    cafe_tipo: "nenhum",
    ci_uma_diaria: false, ci_bloqueia_suites: true,
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    const editQuarto = quartos.find((x) => x.id === r.quarto_id);
    const editIsCI = !!editQuarto?.is_chacara_inteira;
    const editDiarias = Number(r.diarias || 0);
    setForm({
      hospede_id: r.hospede_id || "",
      pousada_id: r.pousada_id || "",
      quarto_id: r.quarto_id || "",
      empresa_id: r.empresa_id || "",
      data_entrada: r.data_entrada || todayStr(),
      data_saida: r.data_saida || "",
      num_pessoas: Number(r.num_pessoas || 1),
      valor_diaria: r.valor_diaria != null ? String(r.valor_diaria) : "",
      observacoes: r.observacoes || "",
      cafe_tipo: r.cafe_tipo || "nenhum",
      ci_uma_diaria: editIsCI && editDiarias === 1,
      ci_bloqueia_suites: r.bloqueia_suites_dia_unico !== false, // default true se null/undef
    });
    setOpen(true);
  };

  const handleDialogOpenChange = (v) => {
    setOpen(v);
    if (!v) { setEditingId(null); setForm(emptyForm); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hospede_id || !form.pousada_id || !form.quarto_id) return toast.error("Preencha hóspede, pousada e quarto");
    if (!form.data_entrada) return toast.error("Informe a data de entrada");

    // FASE 3G+: 1 diária de Chácara Inteira → saída = entrada + 1 dia (para manter overlap padrão)
    let data_saida_final = form.data_saida;
    if (selectedQuartoIsCI && form.ci_uma_diaria) {
      const d = new Date(form.data_entrada + "T00:00:00");
      d.setDate(d.getDate() + 1);
      data_saida_final = d.toISOString().slice(0, 10);
    }
    if (!data_saida_final) return toast.error("Informe a data de saída");
    if (daysBetween(form.data_entrada, data_saida_final) <= 0) {
      return toast.error("Data de saída deve ser posterior à entrada");
    }

    const payload = {
      hospede_id: form.hospede_id,
      pousada_id: form.pousada_id,
      quarto_id: form.quarto_id,
      empresa_id: form.empresa_id || "",
      num_pessoas: Number(form.num_pessoas),
      data_entrada: form.data_entrada,
      data_saida: data_saida_final,
      valor_diaria: form.valor_diaria === "" ? null : Number(form.valor_diaria),
      observacoes: form.observacoes,
      cafe_tipo: form.cafe_tipo || "nenhum",
    };
    if (selectedQuartoIsCI && form.ci_uma_diaria) {
      payload.bloqueia_suites_dia_unico = !!form.ci_bloqueia_suites;
    }
    try {
      if (editingId) {
        await api.patch(`/reservas/${editingId}`, payload);
        toast.success("Reserva atualizada");
      } else {
        await api.post("/reservas", payload);
        toast.success("Reserva criada");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editingId ? "Erro ao atualizar reserva" : "Erro ao criar reserva"));
    }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/reservas/${id}`, { status }); toast.success("Status atualizado"); load(); }
    catch { toast.error("Erro ao atualizar"); }
  };
  const doCheckin = async (r) => {
    if (!window.confirm(`Confirmar chegada de ${r.hospede_nome}? O quarto ${r.quarto_numero} será marcado como ocupado.`)) return;
    try { await api.post(`/reservas/${r.id}/checkin`); toast.success(`Check-in realizado. ${r.hospede_nome} está hospedado.`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro no check-in"); }
  };
  const quickCheckout = async (r) => {
    if (!window.confirm(`Confirmar check-out de ${r.hospede_nome}? O quarto ${r.quarto_numero} será liberado.`)) return;
    try { await api.post(`/reservas/${r.id}/checkout`); toast.success(`Check-out realizado. Quarto ${r.quarto_numero} liberado.`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro no check-out"); }
  };
  const sendWhatsApp = (r) => {
    const empresaLine = r.empresa_nome ? `\n• Empresa: ${r.empresa_nome}` : "";
    const pessoasLine = r.num_pessoas ? `\n• Pessoas: ${r.num_pessoas}` : "";
    const text =
      `*Voucher de Reserva — Rede Hospeda*\n\n` +
      `Olá ${r.hospede_nome}!\n\n` +
      `Sua reserva está confirmada:\n` +
      `• Pousada: ${r.pousada_nome}\n` +
      `• Quarto: ${r.quarto_numero}\n` +
      `• Entrada: ${fmtDate(r.data_entrada)}\n` +
      `• Saída:   ${fmtDate(r.data_saida)}\n` +
      `• Diárias: ${r.diarias}\n` +
      `• Valor total: ${brl(r.valor_total)}` +
      pessoasLine + empresaLine +
      `\n\nQualquer dúvida, estamos à disposição.`;
    const url = buildWaLink(r.hospede_telefone, text);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPagamento = async (r) => {
    setPagReserva(r);
    setPagForm({ valor: "", data: todayStr(), forma: "pix", observacao: "", conta_financeira_id: "" });
    try {
      const { data } = await api.get(`/reservas/${r.id}/pagamentos`);
      setPagList(data || []);
    } catch { setPagList([]); }
    setPagOpen(true);
  };

  const submitPagamento = async (e) => {
    e.preventDefault();
    if (!pagReserva) return;
    const valor = Number(pagForm.valor);
    if (!valor || valor <= 0) return toast.error("Informe um valor maior que zero");
    if (!pagForm.data) return toast.error("Informe a data do pagamento");
    if (!pagForm.conta_financeira_id) return toast.error("Selecione a Conta Financeira do recebimento");
    try {
      await api.post(`/reservas/${pagReserva.id}/pagamentos`, {
        valor, data: pagForm.data, forma: pagForm.forma, observacao: pagForm.observacao || "",
        conta_financeira_id: pagForm.conta_financeira_id,
      });
      toast.success("Pagamento registrado");
      setPagOpen(false);
      setPagReserva(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar pagamento");
    }
  };

  const estornarPagamento = async (pid) => {
    const motivo = window.prompt(
      "Motivo do estorno (obrigatório):",
      ""
    );
    if (motivo === null) return; // usuário cancelou
    const motivoLimpo = (motivo || "").trim();
    if (!motivoLimpo) {
      toast.error("Motivo é obrigatório para estornar um pagamento.");
      return;
    }
    try {
      await api.delete(`/pagamentos/${pid}`, { data: { motivo: motivoLimpo } });
      toast.success("Pagamento estornado (histórico preservado)");
      if (pagReserva) {
        const { data } = await api.get(`/reservas/${pagReserva.id}/pagamentos`);
        setPagList(data || []);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao estornar");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir esta reserva?")) return;
    try { await api.delete(`/reservas/${id}`); toast.success("Reserva removida"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  const capacidadeQuarto = selectedQuarto?.capacidade || 6;
  const pessoasOptions = PESSOAS_SLOTS.filter((n) => n <= Math.max(capacidadeQuarto, 6));

  return (
    <div>
      <PageHeader
        eyebrow="Estadias"
        title="Reservas"
        description="Vincule hóspedes a quartos com número de pessoas, empresa contratante e datas. O sistema aplica o preço automaticamente."
        actions={
          <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-reserva" onClick={openNew} className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                <Plus size={16} className="mr-2" /> Nova reserva
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Editar reserva" : "Nova reserva"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Hóspede</Label>
                    <Select value={form.hospede_id} onValueChange={handleHospedeChange}>
                      <SelectTrigger data-testid="select-reserva-hospede"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {hospedes.filter((h) => h.id).map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}
                        {hospedes.length === 0 && <div className="px-3 py-2 text-sm text-stone-500">Cadastre um hóspede antes.</div>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Empresa contratante (opcional)</Label>
                    <Select
                      value={form.empresa_id || NONE}
                      onValueChange={(v) => setForm({ ...form, empresa_id: v === NONE ? "" : v })}
                    >
                      <SelectTrigger data-testid="select-reserva-empresa">
                        <SelectValue placeholder="Sem empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Sem empresa (pessoa física)</SelectItem>
                        {empresas.filter((e) => e.id).map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Pousada</Label>
                    <Select value={form.pousada_id} onValueChange={(v) => setForm({ ...form, pousada_id: v, quarto_id: "" })}>
                      <SelectTrigger data-testid="select-reserva-pousada"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {pousadas.filter((p) => p.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quarto</Label>
                    <Select value={form.quarto_id} onValueChange={(v) => setForm({ ...form, quarto_id: v })} disabled={!form.pousada_id}>
                      <SelectTrigger data-testid="select-reserva-quarto"><SelectValue placeholder={form.pousada_id ? "Selecione um quarto" : "Escolha a pousada"} /></SelectTrigger>
                      <SelectContent>
                        {isPousadaChacara && chacaraInteiraQuarto && (
                          <>
                            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#083A6B] font-semibold">
                              Chácara inteira
                            </div>
                            <SelectItem
                              key={chacaraInteiraQuarto.id}
                              value={chacaraInteiraQuarto.id}
                              data-testid={`select-item-chacara-inteira-${chacaraInteiraQuarto.id}`}
                            >
                              ⭐ Chácara Inteira · cap. {chacaraInteiraQuarto.capacidade}
                            </SelectItem>
                            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-stone-500 font-semibold border-t border-stone-100 mt-1">
                              Quartos individuais
                            </div>
                          </>
                        )}
                        {permiteReservaInteira && pousadaInteiraQuarto && (
                          <>
                            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#8B6A1F] font-semibold">
                              Pousada inteira
                            </div>
                            <SelectItem
                              key={pousadaInteiraQuarto.id}
                              value={pousadaInteiraQuarto.id}
                              data-testid={`select-item-pousada-inteira-${pousadaInteiraQuarto.id}`}
                            >
                              🏨 Pousada Inteira · cap. {pousadaInteiraQuarto.capacidade}
                            </SelectItem>
                            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-stone-500 font-semibold border-t border-stone-100 mt-1">
                              Quartos individuais
                            </div>
                          </>
                        )}
                        {quartosIndividuais.filter((q) => q.id).map((q) => (
                          <SelectItem key={q.id} value={q.id}>
                            Nº {q.numero} · {q.tipo} · cap. {q.capacidade} {q.status === "ocupado" ? "· (ocupado)" : ""}
                          </SelectItem>
                        ))}
                        {isPousadaChacara && !chacaraInteiraQuarto && (
                          <div className="px-3 py-2 text-xs text-stone-500">
                            Chácara sem "Chácara Inteira" cadastrada. Cadastre em /quartos para vender por inteiro.
                          </div>
                        )}
                        {permiteReservaInteira && !pousadaInteiraQuarto && (
                          <div data-testid="aviso-pi-sem-container" className="px-3 py-2 text-xs text-[#8B6A1F] bg-[#FDF3E3]">
                            Pousada opt-in sem container "Pousada Inteira" cadastrado. Cadastre em /quartos para vender por inteiro.
                          </div>
                        )}
                        {form.pousada_id && quartosByPousada.length === 0 && (
                          <div className="px-3 py-2 text-sm text-stone-500">Nenhum quarto disponível.</div>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedQuartoIsPI && (
                      <div data-testid="aviso-pi-selecionada" className="mt-2 p-3 rounded-md border border-[#8B6A1F]/30 bg-[#FDF3E3] text-[11px] text-[#5c460f]">
                        <strong>Pousada Inteira selecionada.</strong> Esta reserva ocupará todos os quartos físicos desta pousada no período — nenhuma outra reserva individual poderá ser criada em conflito.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2"><Users size={12} /> Número de pessoas</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="pessoas-selector">
                    {pessoasOptions.map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`btn-pessoas-${n}`}
                        onClick={() => setForm({ ...form, num_pessoas: n })}
                        className={`h-10 min-w-[52px] px-3 rounded-md border text-sm transition-colors ${
                          Number(form.num_pessoas) === n
                            ? "bg-[#083A6B] text-white border-[#083A6B]"
                            : "bg-white text-stone-700 border-stone-200 hover:border-[#083A6B]"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {selectedQuarto && (
                    <p className="text-xs text-stone-500 mt-2">
                      {precoDoNumeroPessoas
                        ? <>Preço tabelado para {form.num_pessoas} {form.num_pessoas === 1 ? "pessoa" : "pessoas"}: <b className="text-[#083A6B]">{brl(precoDoNumeroPessoas)}</b>/diária</>
                        : <>Sem preço específico para {form.num_pessoas} {form.num_pessoas === 1 ? "pessoa" : "pessoas"} — usando valor padrão do quarto.</>}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Café da manhã</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="cafe-selector">
                    {[
                      { key: "nenhum", label: "Sem café", price: 0 },
                      { key: "simples", label: "Café simples · Panificadora Jacaré", price: 22 },
                      { key: "donna_italia", label: "Café Donna Italia", price: 32 },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        data-testid={`btn-cafe-${opt.key}`}
                        onClick={() => setForm({ ...form, cafe_tipo: opt.key })}
                        className={`text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                          form.cafe_tipo === opt.key
                            ? "bg-[#083A6B] text-white border-[#083A6B]"
                            : "bg-white text-stone-700 border-stone-200 hover:border-[#083A6B]"
                        }`}
                      >
                        <div className="font-medium">{opt.label}</div>
                        <div className={`text-[10px] mt-0.5 ${form.cafe_tipo === opt.key ? "text-white/80" : "text-stone-500"}`}>
                          {opt.price === 0 ? "Sem custo adicional" : `+ ${brl(opt.price)}/pessoa/diária`}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Data de entrada</Label>
                    <Input type="date" data-testid="input-reserva-entrada" value={form.data_entrada}
                      onChange={(e) => setForm({ ...form, data_entrada: e.target.value })} />
                  </div>
                  {!(selectedQuartoIsCI && form.ci_uma_diaria) && (
                    <div>
                      <Label>Data de saída</Label>
                      <Input type="date" data-testid="input-reserva-saida" value={form.data_saida}
                        onChange={(e) => setForm({ ...form, data_saida: e.target.value })} />
                    </div>
                  )}
                  <div>
                    <Label>Sobrescrever diária (R$)</Label>
                    <Input type="number" min="0" step="0.01" data-testid="input-reserva-valor"
                      value={form.valor_diaria}
                      placeholder={suggestedValor ? String(suggestedValor) : "0"}
                      onChange={(e) => setForm({ ...form, valor_diaria: e.target.value })} />
                  </div>
                </div>

                {selectedQuartoIsCI && (
                  <div data-testid="chacara-modalidade-block" className="border border-[#083A6B]/30 bg-[#EAF0EA]/40 rounded-md p-4 space-y-3">
                    <div className="text-[11px] uppercase tracking-widest text-[#083A6B] font-semibold">
                      Modalidade da Chácara Inteira
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-stone-800">
                          {form.ci_uma_diaria
                            ? "Chácara Inteira — 1 diária (09:00 às 19:00)"
                            : "Chácara Inteira — 2+ diárias — inclui uso das 4 suítes"}
                        </div>
                        <div className="text-[11px] text-stone-600 mt-0.5">
                          {form.ci_uma_diaria
                            ? "Diária avulsa sem pernoite. Não vira o dia."
                            : "Reserva com pernoite. As 4 suítes ficam bloqueadas automaticamente durante todo o período."}
                        </div>
                      </div>
                      <Switch
                        data-testid="switch-ci-uma-diaria"
                        checked={form.ci_uma_diaria}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, ci_uma_diaria: v }))}
                      />
                    </div>
                    {form.ci_uma_diaria && (
                      <div className="flex items-start justify-between gap-3 pt-3 border-t border-[#083A6B]/20">
                        <div>
                          <div className="text-sm font-medium text-stone-800">Bloquear as 4 suítes durante essa diária?</div>
                          <div className="text-[11px] text-stone-600 mt-0.5">
                            {form.ci_bloqueia_suites
                              ? "As suítes ficam indisponíveis para venda individual no dia."
                              : "As suítes permanecem disponíveis para venda individual no mesmo dia."}
                            {" "}Padrão configurado em /pousadas.
                          </div>
                        </div>
                        <Switch
                          data-testid="switch-ci-bloqueia-suites"
                          checked={form.ci_bloqueia_suites}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, ci_bloqueia_suites: v }))}
                        />
                      </div>
                    )}
                  </div>
                )}

                {tarifaSuggest && form.valor_diaria === "" && (
                  <div data-testid="tarifa-suggest" className="flex items-center gap-3 bg-[#FBEDD1] border border-[#E9C46A] rounded-md px-4 py-3">
                    <Tag size={16} className="text-[#8B6A1F]" />
                    <div className="text-sm text-[#5c4a10]">
                      <span className="font-semibold">Tarifa aplicada: {tarifaSuggest.nome}</span>
                      {" · "}{brl(tarifaSuggest.valor_diaria)}/diária (sobrepõe preço por pessoa)
                    </div>
                  </div>
                )}

                <div>
                  <Label>Observações</Label>
                  <Input data-testid="input-reserva-obs" value={form.observacoes}
                    onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                </div>

                <div className="bg-stone-50 border border-stone-200 rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">Diárias</div>
                    <div className="font-serif text-2xl">{diarias}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">Diária aplicada</div>
                    <div className="font-serif text-2xl">{brl(effValorDiaria)}</div>
                  </div>
                  {cafeTotal > 0 && (
                    <div data-testid="reserva-cafe-total">
                      <div className="text-xs uppercase tracking-widest text-stone-500">Café da manhã</div>
                      <div className="font-serif text-xl text-[#8B6A1F]">+ {brl(cafeTotal)}</div>
                      <div className="text-[10px] text-stone-500">{form.num_pessoas} × {diarias} × {brl(cafeValorPessoa)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">Total</div>
                    <div data-testid="reserva-total" className="font-serif text-3xl text-[#083A6B]">{brl(total)}</div>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-reserva" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                    {editingId ? "Salvar alterações" : "Confirmar reserva"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 bg-white border border-stone-200 rounded-lg p-3" data-testid="reservas-filtros">
        <div className="flex items-center gap-1.5 text-stone-500 text-xs uppercase tracking-widest mr-1">
          <Filter size={13} /> Filtros
        </div>
        <div className="min-w-[160px]">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Origem</Label>
          <Select
            value={filtroOrigem || FILTER_NONE}
            onValueChange={(v) => updateFiltro("origem", v)}
          >
            <SelectTrigger data-testid="filtro-origem" className="h-9">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_NONE} data-testid="filtro-origem-item-todas">Todas</SelectItem>
              {ORIGEM_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`filtro-origem-item-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select
            value={filtroStatus || FILTER_NONE}
            onValueChange={(v) => updateFiltro("status", v)}
          >
            <SelectTrigger data-testid="filtro-status" className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_NONE} data-testid="filtro-status-item-todos">Todos</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`filtro-status-item-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFiltros && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={limparFiltros}
            data-testid="btn-limpar-filtros"
            className="h-9"
          >
            Limpar filtros
          </Button>
        )}
        {hasFiltros && (
          <div className="text-xs text-stone-500 ml-auto" data-testid="reservas-filtros-contagem">
            {reservas.length} resultado(s)
          </div>
        )}
      </div>

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Hóspede / Empresa</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada / Quarto</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pessoas</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Entrada</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Saída</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Diárias</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Total</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservas.map((r) => {
              const isPreReservaSite = r.origem === "site" && r.status === "pre-reserva";
              return (
              <TableRow
                key={r.id}
                data-testid={`reserva-row-${r.id}`}
                data-origem={r.origem || "admin"}
                className={isPreReservaSite ? "bg-[#F4F8FD] hover:bg-[#EAF0FA]" : ""}
              >
                <TableCell>
                  <div className="font-medium text-stone-800 flex items-center gap-2">
                    <span>{r.hospede_nome}</span>
                    <OrigemPill origem={r.origem || "admin"} testid={`origem-pill-${r.id}`} />
                  </div>
                  {isPreReservaSite && (
                    <div className="text-[10px] uppercase tracking-widest text-[#083A6B] mt-0.5" data-testid={`pre-reserva-site-tag-${r.id}`}>
                      Pré-reserva do site
                    </div>
                  )}
                  {r.empresa_nome && (
                    <div className="text-xs text-[#D4A652] flex items-center gap-1 mt-0.5" data-testid={`empresa-tag-${r.id}`}>
                      <Briefcase size={11} /> {r.empresa_nome}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-stone-700">
                  <div>{r.pousada_nome}</div>
                  <div className="text-xs text-stone-500">Quarto {r.quarto_numero}</div>
                </TableCell>
                <TableCell><span className="inline-flex items-center gap-1 text-sm tabular-nums"><Users size={12} />{r.num_pessoas || 1}</span></TableCell>
                <TableCell>{fmtDate(r.data_entrada)}</TableCell>
                <TableCell>{fmtDate(r.data_saida)}</TableCell>
                <TableCell className="font-medium tabular-nums text-stone-800">{r.diarias}</TableCell>
                <TableCell className="font-medium text-[#083A6B] tabular-nums whitespace-nowrap">{brl(r.valor_total)}</TableCell>
                <TableCell><StatusPill status={r.status} testid={`status-reserva-${r.id}`} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button data-testid={`btn-detalhes-${r.id}`}
                    onClick={() => { setReservaDetalhe(r); setDetalheOpen(true); }}
                    title="Detalhes da reserva" className="text-stone-500 hover:text-[#083A6B] p-1 mr-1">
                    <Eye size={16} />
                  </button>
                  {(r.status === "pre-reserva" || r.status === "hospedado" || r.status === "ativa") && (
                    <button data-testid={`btn-editar-${r.id}`}
                      onClick={() => openEdit(r)}
                      title="Editar reserva" className="text-stone-500 hover:text-[#083A6B] p-1 mr-1">
                      <Pencil size={16} />
                    </button>
                  )}
                  {r.status === "pre-reserva" && (
                    <Button data-testid={`btn-checkin-${r.id}`} onClick={() => doCheckin(r)} size="sm"
                      className="mr-2 h-8 bg-[#083A6B] hover:bg-[#052B50] text-white rounded-md px-3">
                      <LogIn size={14} className="mr-1.5" /> Check-in
                    </Button>
                  )}
                  {(r.status === "hospedado" || r.status === "ativa") && (
                    <Button data-testid={`btn-checkout-${r.id}`} onClick={() => quickCheckout(r)} size="sm"
                      className="mr-2 h-8 bg-[#D4A652] hover:bg-[#a4462e] text-white rounded-md px-3">
                      <LogOut size={14} className="mr-1.5" /> Check-out
                    </Button>
                  )}
                  <button data-testid={`btn-pdf-${r.id}`} onClick={() => generateVoucherPDF(r)} title="Voucher em PDF" className="text-stone-500 hover:text-[#083A6B] p-1">
                    <FileDown size={16} />
                  </button>
                  <button data-testid={`btn-recibo-${r.id}`} onClick={() => generateReceiptPDF(r)} title="Emitir recibo em PDF" className="text-stone-500 hover:text-[#D4A652] p-1">
                    <Receipt size={16} />
                  </button>
                  <button data-testid={`btn-whatsapp-${r.id}`} onClick={() => sendWhatsApp(r)} title="Voucher por WhatsApp" className="text-[#25D366] hover:text-[#1ebe57] p-1">
                    <MessageCircle size={16} />
                  </button>
                  {(r.status === "pre-reserva" || r.status === "hospedado" || r.status === "ativa") && (
                    <button data-testid={`btn-pagamento-${r.id}`} onClick={() => openPagamento(r)}
                      title="Registrar pagamento" className="text-stone-500 hover:text-[#083A6B] p-1 mr-1">
                      <DollarSign size={16} />
                    </button>
                  )}
                  {(r.status === "pre-reserva" || r.status === "hospedado" || r.status === "ativa") && (
                    <button data-testid={`btn-cancelar-${r.id}`} onClick={() => changeStatus(r.id, "cancelada")} title="Cancelar" className="text-stone-500 hover:text-[#D4A652] p-1">
                      <XCircle size={16} />
                    </button>
                  )}
                  <button data-testid={`btn-delete-reserva-${r.id}`} onClick={() => remove(r.id)} className="text-stone-400 hover:text-[#D4A652] p-1">
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
              );
            })}
            {reservas.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-stone-500" data-testid="reservas-empty-state">
                  {hasFiltros ? "Nenhuma reserva encontrada para os filtros aplicados." : "Nenhuma reserva registrada."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={detalheOpen} onOpenChange={setDetalheOpen}>
        <DialogContent data-testid="dialog-detalhes-reserva" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              Detalhes da reserva
              {reservaDetalhe && <StatusPill status={reservaDetalhe.status} testid="detalhe-status" />}
            </DialogTitle>
          </DialogHeader>
          {reservaDetalhe && (
            <div className="space-y-6 text-sm">
              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Hóspede</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-stone-500 text-xs">Nome</div><div className="text-stone-800 font-medium" data-testid="detalhe-hospede">{reservaDetalhe.hospede_nome || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Telefone</div><div className="text-stone-800" data-testid="detalhe-telefone">{reservaDetalhe.hospede_telefone || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Documento</div><div className="text-stone-800" data-testid="detalhe-documento">{reservaDetalhe.hospede_documento || "—"}</div></div>
                  {reservaDetalhe.empresa_nome && (
                    <div><div className="text-stone-500 text-xs">Empresa</div><div className="text-[#D4A652] font-medium flex items-center gap-1" data-testid="detalhe-empresa"><Briefcase size={12} />{reservaDetalhe.empresa_nome}</div></div>
                  )}
                </div>
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Hospedagem</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-stone-500 text-xs">Pousada</div><div className="text-stone-800 font-medium" data-testid="detalhe-pousada">{reservaDetalhe.pousada_nome || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Endereço da pousada</div><div className="text-stone-700 text-xs" data-testid="detalhe-endereco">{reservaDetalhe.pousada_endereco || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Quarto</div><div className="text-stone-800 font-medium">Nº {reservaDetalhe.quarto_numero || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Tipo do quarto</div><div className="text-stone-800">{reservaDetalhe.quarto_tipo || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Nº de pessoas</div><div className="text-stone-800 flex items-center gap-1"><Users size={12} />{reservaDetalhe.num_pessoas || 1}</div></div>
                  <div><div className="text-stone-500 text-xs">Diárias</div><div className="text-stone-800 font-medium">{reservaDetalhe.diarias || 0}</div></div>
                  <div><div className="text-stone-500 text-xs">Data de entrada</div><div className="text-stone-800">{fmtDate(reservaDetalhe.data_entrada)}</div></div>
                  <div><div className="text-stone-500 text-xs">Data de saída</div><div className="text-stone-800">{fmtDate(reservaDetalhe.data_saida)}</div></div>
                </div>
              </section>

                <section className="bg-stone-50 border border-stone-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-3">Financeiro</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-stone-500 text-xs">Valor da diária</div><div className="text-stone-800 font-medium">{brl(reservaDetalhe.valor_diaria)}</div></div>
                  <div><div className="text-stone-500 text-xs">Valor total</div><div className="font-serif text-xl text-[#083A6B]" data-testid="detalhe-total">{brl(reservaDetalhe.valor_total)}</div></div>
                  <div><div className="text-stone-500 text-xs">Valor pago</div><div className="text-stone-800 font-medium" data-testid="detalhe-pago">{brl(reservaDetalhe.valor_pago)}</div></div>
                  <div><div className="text-stone-500 text-xs">Saldo a pagar</div><div className="font-serif text-lg text-[#D4A652]" data-testid="detalhe-saldo">{brl(Math.max(0, Number(reservaDetalhe.valor_total || 0) - Number(reservaDetalhe.valor_pago || 0)))}</div></div>
                  <div><div className="text-stone-500 text-xs">Status do pagamento</div><div className="text-stone-800 capitalize" data-testid="detalhe-status-pagamento">{reservaDetalhe.status_pagamento || "—"}</div></div>
                  <div><div className="text-stone-500 text-xs">Forma de pagamento</div><div className="text-stone-800">{reservaDetalhe.forma_pagamento || "—"}</div></div>
                  {reservaDetalhe.data_faturamento && (
                    <div><div className="text-stone-500 text-xs">Data de faturamento</div><div className="text-stone-800">{fmtDate(reservaDetalhe.data_faturamento)}</div></div>
                  )}
                </div>

                <SinalSection
                  reserva={reservaDetalhe}
                  onChange={(atualizada) => { setReservaDetalhe(atualizada); load(); }}
                  contasFinanceiras={contasFinanceiras}
                />
              </section>

              {(reservaDetalhe.tarifa_nome || reservaDetalhe.observacoes) && (
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Detalhes adicionais</div>
                  {reservaDetalhe.tarifa_nome && (
                    <div className="mb-2"><div className="text-stone-500 text-xs">Tarifa aplicada</div><div className="text-stone-800 flex items-center gap-1" data-testid="detalhe-tarifa"><Tag size={12} />{reservaDetalhe.tarifa_nome}</div></div>
                  )}
                  {reservaDetalhe.observacoes && (
                    <div><div className="text-stone-500 text-xs">Observações</div><div className="text-stone-700 whitespace-pre-wrap" data-testid="detalhe-observacoes">{reservaDetalhe.observacoes}</div></div>
                  )}
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="btn-fechar-detalhes" variant="outline" onClick={() => setDetalheOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pagOpen} onOpenChange={(v) => { setPagOpen(v); if (!v) { setPagReserva(null); setPagList([]); } }}>
        <DialogContent data-testid="dialog-pagamento" className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Registrar pagamento</DialogTitle>
          </DialogHeader>
          {pagReserva && (() => {
            const total = Number(pagReserva.valor_total || 0);
            const pago = pagList.reduce((s, p) => s + Number(p.valor || 0), 0);
            const saldo = Math.max(0, total - pago);
            return (
              <div className="space-y-4 text-sm">
                <div className="bg-stone-50 border border-stone-200 rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">{pagReserva.hospede_nome}</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><div className="text-stone-500 text-xs">Total</div><div className="font-serif text-lg text-stone-800" data-testid="pag-total">{brl(total)}</div></div>
                    <div><div className="text-stone-500 text-xs">Pago</div><div className="font-serif text-lg text-[#083A6B]" data-testid="pag-pago">{brl(pago)}</div></div>
                    <div><div className="text-stone-500 text-xs">Saldo</div><div className="font-serif text-lg text-[#D4A652]" data-testid="pag-saldo">{brl(saldo)}</div></div>
                  </div>
                </div>

                {saldo > 0 ? (
                  <form onSubmit={submitPagamento} className="space-y-3 border border-stone-200 rounded-md p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Valor recebido</Label>
                        <Input type="number" step="0.01" min="0.01" max={saldo} data-testid="input-pag-valor"
                          value={pagForm.valor}
                          onChange={(e) => setPagForm({ ...pagForm, valor: e.target.value })} />
                      </div>
                      <div>
                        <Label>Data</Label>
                        <Input type="date" data-testid="input-pag-data" value={pagForm.data}
                          onChange={(e) => setPagForm({ ...pagForm, data: e.target.value })} />
                      </div>
                      <div>
                        <Label>Forma</Label>
                        <Select value={pagForm.forma} onValueChange={(v) => setPagForm({ ...pagForm, forma: v })}>
                          <SelectTrigger data-testid="select-pag-forma"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pix">Pix</SelectItem>
                            <SelectItem value="cartao">Cartão</SelectItem>
                            <SelectItem value="dinheiro">Dinheiro</SelectItem>
                            <SelectItem value="boleto">Boleto</SelectItem>
                            <SelectItem value="transferencia">Transferência</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Observação</Label>
                        <Input data-testid="input-pag-obs" value={pagForm.observacao}
                          onChange={(e) => setPagForm({ ...pagForm, observacao: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <Label>Conta Financeira *</Label>
                        <Select value={pagForm.conta_financeira_id}
                          onValueChange={(v) => setPagForm({ ...pagForm, conta_financeira_id: v })}>
                          <SelectTrigger data-testid="select-pag-conta-financeira"><SelectValue placeholder="Selecione a conta de recebimento" /></SelectTrigger>
                          <SelectContent>
                            {contasFinanceiras.length === 0 ? (
                              <SelectItem value="__none__" disabled>Nenhuma conta ativa cadastrada</SelectItem>
                            ) : (
                              contasFinanceiras.map((c) => (
                                <SelectItem key={c.id} value={c.id} data-testid={`opt-conta-fin-${c.id}`}>
                                  {c.nome}{c.banco ? ` — ${c.banco}` : ""}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" data-testid="btn-submit-pagamento"
                      disabled={!pagForm.conta_financeira_id}
                      className="w-full bg-[#083A6B] hover:bg-[#052B50] text-white disabled:opacity-50">
                      Confirmar recebimento
                    </Button>
                  </form>
                ) : (
                  <div className="bg-[#EAF0EA] text-[#083A6B] text-sm rounded-md px-4 py-3">
                    Reserva quitada. Sem saldo para novos pagamentos.
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Histórico de pagamentos</div>
                  {pagList.length === 0 ? (
                    <div className="text-xs text-stone-400 py-2">Nenhum pagamento registrado.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {pagList.map((p) => (
                        <div key={p.id} data-testid={`pag-item-${p.id}`} className="flex items-center gap-3 border-b border-stone-100 pb-2 text-sm">
                          <div className="text-xs text-stone-500 w-20">{fmtDate(p.data)}</div>
                          <div className="text-stone-800 font-medium w-28">{brl(p.valor)}</div>
                          <div className="text-xs text-stone-500 capitalize flex-1">{p.forma}{p.observacao ? ` · ${p.observacao}` : ""}</div>
                          <button data-testid={`btn-estornar-${p.id}`} onClick={() => estornarPagamento(p.id)}
                            title="Estornar" className="text-stone-400 hover:text-[#D4A652]"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
